'use client';

import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useT } from '@/lib/i18n';
import { api } from '@/lib/api';
import { Library } from '@/types';

interface NavItemDef {
  label: string;
  icon: string;
  href: string;
  count?: number;
}

// Primary items are resolved inside the component body so `t()` is in scope.
// `nav.models` doesn't exist in the locale files yet — fall back to the
// hardcoded "AI models" label below if it's missing.
const PRIMARY_ITEM_SPECS: { key: string; icon: string; href: string; fallback?: string }[] = [
  { key: 'nav.home',        icon: 'home',                   href: '/' },
  { key: 'nav.explore',     icon: 'explore',                href: '/explore' },
  { key: 'nav.trending',    icon: 'local_fire_department',  href: '/trending' },
  { key: 'nav.map',         icon: 'public',                 href: '/map', fallback: 'World map' },
  { key: 'nav.collections', icon: 'library_books',          href: '/collections' },
  { key: 'nav.all_tags',    icon: 'sell',                   href: '/tags' },
  { key: 'nav.models',      icon: 'smart_toy',              href: '/models', fallback: 'AI models' },
];

// Fallback library set — used while the API call is in flight or if the
// backend is unreachable. Matches the default seed admins get on fresh DBs.
const FALLBACK_LIBRARIES: Pick<Library, 'slug' | 'name' | 'icon'>[] = [
  { slug: 'writing',   name: 'Writing',   icon: 'edit_note' },
  { slug: 'coding',    name: 'Coding',    icon: 'code' },
  { slug: 'art',       name: 'Art',       icon: 'palette' },
  { slug: 'business',  name: 'Business',  icon: 'business_center' },
  { slug: 'education', name: 'Education', icon: 'school' },
];

function SidebarRailInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user } = useAuth();
  const t = useT();

  // Load enabled libraries from the API so admins can add/remove/rename
  // without a redeploy. Fall back to the seed defaults on error so the
  // sidebar is never empty.
  const [libraries, setLibraries] = useState<Pick<Library, 'slug' | 'name' | 'icon'>[]>(FALLBACK_LIBRARIES);
  useEffect(() => {
    api.libraries.list()
      .then((rows) => {
        if (rows.length > 0) {
          setLibraries(rows.map((r) => ({ slug: r.slug, name: r.name, icon: r.icon ?? 'folder' })));
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const currentCategory = pathname === '/' ? (searchParams.get('category') ?? '') : '';
  const isHomeAll = pathname === '/' && !currentCategory;

  const goToLibrary = (slug: string) => {
    router.push(`/library/${slug}`);
  };

  const myPromptsCount = 0; // placeholder — we don't fetch this globally
  const savedItems: NavItemDef[] = [
    { label: t('nav.saved'),      icon: 'bookmark',    href: '/my/saved' },
    { label: t('nav.my_prompts'), icon: 'grid_view',   href: '/my/prompts', count: myPromptsCount || undefined },
  ];
  const primaryItems: NavItemDef[] = PRIMARY_ITEM_SPECS.map((s) => {
    const translated = t(s.key);
    // i18n returns the key itself if missing — detect that and use the
    // fallback label so we don't render "nav.models" verbatim.
    const label = (translated === s.key && s.fallback) ? s.fallback : translated;
    return { label, icon: s.icon, href: s.href };
  });

  return (
    <aside className="sidebar" aria-label="Primary navigation">
      <Link href="/" className="sidebar-logo no-underline">
        <span className="mark">P</span>
        <span>Prompt Save</span>
      </Link>

      <Link
        href={user ? '/create' : '/auth/login'}
        className="ps-btn ps-btn-accent no-underline"
        style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }}
      >
        <span className="material-symbols-outlined ps-icon">add</span>
        {t('nav.new_prompt')}
      </Link>

      {primaryItems.map((it) => (
        <NavItem key={it.href} {...it}
          active={pathname === it.href || (it.href === '/' && isHomeAll)}
        />
      ))}

      {user && (
        <NavItem
          label={t('nav.following')}
          icon="group"
          href="/following"
          active={pathname === '/following'}
        />
      )}

      {user && savedItems.map((it) => (
        <NavItem key={it.href} {...it} active={pathname === it.href} />
      ))}

      {/* Admin link removed from the user sidebar — the admin console is now a
          completely separate app at /admin with its own login, not a page that
          regular users can discover via their own session. */}

      <div className="sidebar-section">{t('nav.libraries')}</div>
      {libraries.map((lib) => {
        const href = `/library/${lib.slug}`;
        return (
          <NavItem
            key={lib.slug}
            label={lib.name}
            icon={lib.icon ?? 'folder'}
            href={href}
            active={pathname === href}
            onClick={(e) => { e.preventDefault(); goToLibrary(lib.slug); }}
          />
        );
      })}

      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {user ? (
          <NavItem label={t('nav.profile')} icon="person" href={`/profile/${user.username}`}
            active={pathname === `/profile/${user.username}`} />
        ) : (
          <NavItem label={t('nav.sign_in')} icon="login" href="/auth/login"
            active={pathname.startsWith('/auth')} />
        )}
      </div>
    </aside>
  );
}

export default function SidebarRail() {
  return (
    <Suspense fallback={<aside className="sidebar" />}>
      <SidebarRailInner />
    </Suspense>
  );
}

function NavItem({ label, icon, href, count, active, onClick }: NavItemDef & {
  active: boolean;
  onClick?: (e: React.MouseEvent) => void;
}) {
  return (
    <Link
      href={href}
      className="nav-item no-underline"
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
    >
      <span className="material-symbols-outlined nav-icon">{icon}</span>
      <span>{label}</span>
      {count ? <span className="count">{count}</span> : null}
    </Link>
  );
}
